import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Transaction } from '@asia/shared';
import { useSession } from './useSession';
import { onrampListApi, onrampToTransaction, type OnrampOrder } from '../lib/onramp';

/** The user's fiat purchases. Settlement is announced over the socket, so this
 *  doesn't poll — useRealtime refreshes it when the provider reports back. */
export function useOnrampOrders() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['onrampOrders'],
    enabled: !!token,
    queryFn: () => onrampListApi(),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fold fiat purchases into an on-chain history.
 *
 * A completed purchase already appears on chain as an ordinary deposit, so it
 * must not be listed twice: where the provider told us the delivering hash, the
 * existing row is relabelled "Bought" instead of "Received". A purchase still
 * being paid for has no hash yet and gets a row of its own, so it stays visible
 * while the user waits.
 */
export function useHistoryWithPurchases(txs: Transaction[], orders: OnrampOrder[] | undefined) {
  return useMemo(() => {
    if (!orders?.length) return txs;

    const boughtHashes = new Map<string, OnrampOrder>();
    for (const o of orders) {
      if (o.status === 'completed' && o.txHash) boughtHashes.set(o.txHash.toLowerCase(), o);
    }

    const merged = txs.map((tx) => {
      const hash = tx.explorerUrl?.split('/').pop()?.toLowerCase();
      const order = hash ? boughtHashes.get(hash) : undefined;
      return order ? { ...tx, type: 'buy' as const, amountUsd: order.fiatAmount } : tx;
    });

    const settling = orders
      .filter((o) => o.status === 'created' || o.status === 'processing' || (o.status === 'completed' && !o.txHash))
      .map(onrampToTransaction);

    if (!settling.length) return merged;
    return [...settling, ...merged].sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  }, [txs, orders]);
}
